import {
  type BasePlanAttributes,
  type PlanAttributes,
  type PlanDay,
  StandardErrorCodes,
  type UpdatePlanName,
} from '@soulhx/fs-common';
import { db, type LocalPlan } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { PLAN_SYNC_KEY } from './PlanSync.js';
import { SyncFetchError, syncFetch } from './syncFetch.js';
import { toUtcIso } from './timestamps.js';

/*
 * Writes for plan *authoring* (save, publish, new version, rename,
 * delete). These are online-only: they're rare, the server does the
 * validation the author needs to see (versions, permissions, day counts),
 * and there's no locking, so conflicts are reported (409) rather than
 * merged. Each writes the server's response straight into dexie, then
 * triggers a sync (e.g. publishing supersedes older versions, which
 * arrives via the pull). Errors propagate to the caller.
 */

const PLANS_PATH = '/read/pl/p';

/**
 * True when a plan write was rejected because the plan changed (on
 * another device, or by another admin) since it was loaded
 */
export const isPlanConflictError = (error: unknown): boolean =>
  error instanceof SyncFetchError && error.status === 409 && error.errorCode === StandardErrorCodes.PlanConflict;

const storePlan = async (plan: PlanAttributes): Promise<LocalPlan> => {
  const local: LocalPlan = {
    ...plan,
    days: (plan.days ?? []) as PlanDay[],
    updateDate: toUtcIso(plan.updateDate!),
  };
  await db.plans.put(local);
  triggerGlobalSync(PLAN_SYNC_KEY);
  return local;
};

/**
 * Creates (no `planInstanceId`) or updates a plan; for an update, the
 * plan's `updateDate` should be the one it was loaded with
 */
export const savePlan = async (plan: BasePlanAttributes | PlanAttributes): Promise<LocalPlan> =>
  storePlan(await syncFetch<PlanAttributes>({ method: 'POST', path: `${PLANS_PATH}/s`, body: plan }));

export const publishPlan = async (plan: PlanAttributes): Promise<LocalPlan> =>
  storePlan(await syncFetch<PlanAttributes>({ method: 'POST', path: `${PLANS_PATH}/p`, body: plan }));

/**
 * Creates a new, editable (saved) version of a published plan
 */
export const newPlanVersion = async (
  plan: PlanAttributes,
  incrementType: 'major' | 'minor' | 'patch'
): Promise<LocalPlan> =>
  storePlan(
    await syncFetch<PlanAttributes>({
      method: 'POST',
      path: `${PLANS_PATH}/p/newVersion/${incrementType}`,
      body: plan,
    })
  );

export const updatePlanName = async (planInstanceId: string, details: UpdatePlanName): Promise<void> => {
  const updated = await syncFetch<PlanAttributes>({
    method: 'PUT',
    path: `${PLANS_PATH}/updateName/${planInstanceId}`,
    body: details,
  });

  // the response doesn't include the plan's days
  await db.plans.update(planInstanceId, {
    name: updated.name,
    description: updated.description,
    updateDate: toUtcIso(updated.updateDate!),
  });
  triggerGlobalSync(PLAN_SYNC_KEY);
};

/**
 * Deletes a plan. Subscribers keep it (as `deleted`) until they
 * unsubscribe, so if the user is subscribed it's kept locally too.
 */
export const deletePlan = async (planInstanceId: string): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${PLANS_PATH}/${planInstanceId}` });

  await db.transaction('rw', db.plans, db.instantiatedPlans, async () => {
    const subscribed = await db.instantiatedPlans
      .where('planInstanceId')
      .equals(planInstanceId)
      .filter((ip) => ip.syncStatus !== 'pendingDelete')
      .count();

    if (subscribed > 0) {
      await db.plans.update(planInstanceId, { status: 'deleted' });
    } else {
      await db.plans.delete(planInstanceId);
    }
  });
  triggerGlobalSync(PLAN_SYNC_KEY);
};
